import { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Badge, Button, Input, FormControl, FormLabel, useToast, SimpleGrid, Avatar, Divider,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { api } from 'lib/api';
import { useAuth } from 'lib/auth';

export default function Profile() {
  const { account } = useAuth();
  const isAdmin = account?.role === 'admin';
  const name = account?.displayName || account?.username || account?.email || 'Tài khoản';
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px" alignItems="start">
        <Card>
          <Flex align="center" gap="16px" mb="18px">
            <Avatar name={name} bg="brand.500" color="white" w="64px" h="64px" />
            <Box>
              <Text fontWeight="850" fontSize="19px">{name}</Text>
              <Flex align="center" gap="8px" mt="3px">
                <Text fontSize="13px" color="secondaryGray.600">{isAdmin ? `@${account?.username}` : account?.email}</Text>
                <Badge colorScheme={isAdmin ? 'purple' : account?.tier === 'pro' ? 'green' : 'gray'}>
                  {isAdmin ? 'QUẢN TRỊ' : (account?.tier || 'free').toUpperCase()}
                </Badge>
              </Flex>
            </Box>
          </Flex>
          <Divider mb="16px" />
          <Row label={isAdmin ? 'Tên đăng nhập' : 'Email'} value={isAdmin ? account?.username : account?.email} />
          <Row label="Tên hiển thị" value={account?.displayName} />
          <Row label="Vai trò" value={isAdmin ? 'Quản trị viên' : 'Phụ huynh'} />
        </Card>
        <PasswordCard />
        {!isAdmin && <DeleteAccountCard />}
      </SimpleGrid>
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Flex justify="space-between" py="10px" borderBottom="1px solid" borderColor="line">
      <Text color="secondaryGray.600">{label}</Text><Text fontWeight="700">{value || '—'}</Text>
    </Flex>
  );
}

/**
 * Xoá tài khoản (App Store 5.1.1(v) / Play data deletion — Play còn đòi một đường XOÁ trên web).
 * Xoá ngay, cascade toàn bộ con/máy/gói/trận; phải gõ "XOA" để mở nút.
 */
function DeleteAccountCard() {
  const toast = useToast();
  const { logout } = useAuth();
  const [sum, setSum] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/accounts/me/deletion-summary').then(setSum).catch(() => {}); }, []);

  const armed = typed.trim().toUpperCase() === 'XOA';
  const paidPlan = sum?.subscription
    ? ['active', 'in_grace'].includes(sum.subscription.status)
    : (sum?.tier || 'free') !== 'free';

  async function submit(e) {
    e.preventDefault();
    if (!armed) return;
    setBusy(true);
    try {
      await api.del('/accounts/me?confirm=XOA');
      toast({ title: 'Đã xoá tài khoản và toàn bộ dữ liệu', status: 'success', duration: 6000 });
      logout();
    } catch (err) {
      toast({ title: err.message || 'Không xoá được tài khoản', status: 'error' });
      setBusy(false);
    }
  }

  return (
    <Card borderColor="red.300" borderWidth="1px">
      <Text fontWeight="850" fontSize="17px" mb="4px" color="red.500">🗑 Xoá tài khoản</Text>
      <Text fontSize="sm" color="secondaryGray.600" mb="14px">
        Xoá vĩnh viễn tài khoản cùng toàn bộ hồ sơ con, thiết bị, kim cương và lịch sử học. Không khôi phục được.
      </Text>
      {sum && (
        <Box mb="14px">
          <Row label="Hồ sơ con" value={sum.children === 0 ? 'không có' : `${sum.children} bé · ${sum.childNames.join(', ')}`} />
          <Row label="Thiết bị đã liên kết" value={`${sum.devices} máy`} />
          <Row label="Kim cương" value={`${sum.diamonds} 💎`} />
          <Row label="Câu đã làm" value={`${sum.answers}`} />
          <Row label="Trận thách đấu" value={`${sum.battles}`} />
        </Box>
      )}
      {paidPlan && (
        <Text fontSize="sm" color="orange.500" mb="14px">
          ⚠️ Gói đang có hiệu lực KHÔNG tự huỷ khi xoá tài khoản — hãy huỷ trong App Store / Google Play,
          nếu không vẫn bị tiếp tục thu tiền.
        </Text>
      )}
      <Text fontSize="sm" color="secondaryGray.600" mb="14px">
        Máy con trong nhà sẽ tự về gói Free; dữ liệu học lưu trong từng máy vẫn giữ để con học offline.
      </Text>
      <form onSubmit={submit}>
        <FormControl mb="14px">
          <FormLabel fontSize="sm" fontWeight="600">Gõ <b>XOA</b> để xác nhận</FormLabel>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="XOA" autoComplete="off" />
        </FormControl>
        <Button type="submit" colorScheme="red" isDisabled={!armed} isLoading={busy}>
          Xoá vĩnh viễn tài khoản
        </Button>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const toast = useToast();
  const [hasPassword, setHasPassword] = useState(true);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/auth/me').then((m) => setHasPassword(!!m.hasPassword)).catch(() => {}); }, []);

  async function submit(e) {
    e.preventDefault();
    if (next.length < 6) return toast({ title: 'Mật khẩu mới tối thiểu 6 ký tự', status: 'warning' });
    if (next !== confirm) return toast({ title: 'Xác nhận mật khẩu không khớp', status: 'warning' });
    setBusy(true);
    try {
      await api.post('/auth/password', { currentPassword: current || undefined, newPassword: next });
      toast({ title: hasPassword ? 'Đã đổi mật khẩu' : 'Đã đặt mật khẩu', status: 'success' });
      setHasPassword(true); setCurrent(''); setNext(''); setConfirm('');
    } catch (err) { toast({ title: err.message || 'Không đổi được mật khẩu', status: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <Card>
      <Text fontWeight="850" fontSize="17px" mb="4px">🔒 {hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}</Text>
      <Text fontSize="sm" color="secondaryGray.600" mb="16px">
        {hasPassword ? 'Nhập mật khẩu hiện tại rồi đặt mật khẩu mới.' : 'Đặt mật khẩu để đăng nhập bằng email + mật khẩu.'}
      </Text>
      <form onSubmit={submit}>
        {hasPassword && (
          <FormControl mb="12px"><FormLabel fontSize="sm" fontWeight="600">Mật khẩu hiện tại</FormLabel>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••" /></FormControl>
        )}
        <FormControl mb="12px"><FormLabel fontSize="sm" fontWeight="600">Mật khẩu mới</FormLabel>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Tối thiểu 6 ký tự" /></FormControl>
        <FormControl mb="16px"><FormLabel fontSize="sm" fontWeight="600">Xác nhận mật khẩu mới</FormLabel>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Nhập lại" /></FormControl>
        <Button type="submit" colorScheme="brand" isLoading={busy}>{hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}</Button>
      </form>
    </Card>
  );
}
